import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Pencil, Repeat, CopyPlus, Trash2, ChevronDown, ChevronUp } from "lucide-react";

const MODES = [
  { key: "view", label: "Details" },
  { key: "edit", label: "Edit", icon: Pencil },
  { key: "replace", label: "Replace Employee", icon: Repeat },
  { key: "copy", label: "Copy to...", icon: CopyPlus },
  { key: "remove", label: "Remove", icon: Trash2 },
];

/** Opens on click of an EXISTING shift chip in ScheduleGrid - never destructive on the first
 * click (view mode). Edit/Replace/Remove are two API calls under the hood (remove old + add
 * new) since the backend has no PATCH-assignment endpoint; the parent wraps both in a single
 * undo-able action so a mistake here is always one click to reverse. */
export const ShiftDetailDialog = ({ open, onOpenChange, assignment, board, employees, templates, onEdit, onReplace, onCopy, onRemove }) => {
  const [mode, setMode] = useState("view");
  const [showEvidence, setShowEvidence] = useState(false);
  const [form, setForm] = useState({ start_time: "", end_time: "", break_minutes: 0, role: "", crosses_midnight: false, shift_template_id: "" });
  const [replaceEmployeeId, setReplaceEmployeeId] = useState("");
  const [copyDate, setCopyDate] = useState("");
  const [copyEmployeeId, setCopyEmployeeId] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && assignment) {
      setMode("view");
      setShowEvidence(false);
      setForm({
        start_time: assignment.start_time, end_time: assignment.end_time, break_minutes: assignment.break_minutes || 0,
        role: assignment.role || "", crosses_midnight: assignment.crosses_midnight || false, shift_template_id: assignment.shift_template_id || "",
      });
      setReplaceEmployeeId("");
      setCopyDate(assignment.date);
      setCopyEmployeeId(assignment.employee_id);
      setReason("");
    }
  }, [open, assignment]);

  const employeeName = (id) => employees.find((e) => e.id === id)?.legal_name || id;
  const evidence = useMemo(() => board?.employees?.find((e) => e.employee_id === assignment?.employee_id)?.reasons || [], [board, assignment]);

  const applyTemplate = (templateId) => {
    const tpl = templates.find((t) => t.id === templateId);
    setForm((f) => ({ ...f, shift_template_id: templateId, start_time: tpl?.start_time || f.start_time, end_time: tpl?.end_time || f.end_time, crosses_midnight: tpl?.crosses_midnight || false }));
  };

  if (!assignment) return null;

  const run = async (fn) => {
    setSubmitting(true);
    try {
      await fn();
      onOpenChange(false);
    } catch (e) {
      // Error already surfaced via toast by the parent action helper - keep the dialog open.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <DialogContent className="max-w-xl" data-testid="shift-detail-dialog">
        <DialogHeader>
          <DialogTitle>{employeeName(assignment.employee_id)} - {assignment.date}</DialogTitle>
          <DialogDescription>{assignment.start_time}-{assignment.end_time}{assignment.crosses_midnight && " (+1 day)"} - {Math.floor(assignment.duration_minutes / 60)}h{assignment.duration_minutes % 60}m</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5 flex-wrap">
          {MODES.map((m) => (
            <button
              key={m.key}
              data-testid={`shift-detail-mode-${m.key}`}
              onClick={() => setMode(m.key)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border ${mode === m.key ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"} ${m.key === "remove" ? "ml-auto" : ""}`}
            >
              {m.icon && <m.icon size={12} />} {m.label}
            </button>
          ))}
        </div>

        {mode === "view" && (
          <div className="space-y-2">
            <button data-testid="shift-detail-evidence-toggle" onClick={() => setShowEvidence((v) => !v)} className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800">
              View Eligibility Evidence {showEvidence ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            {showEvidence && (
              <div className="space-y-1 bg-gray-50 rounded-lg p-3" data-testid="shift-detail-evidence-list">
                {evidence.length === 0 && <p className="text-xs text-gray-500">No eligibility notes for this employee.</p>}
                {evidence.map((r, i) => (
                  <p key={i} className={`text-[11px] ${r.severity === "BLOCKING" ? "text-red-600" : r.severity === "WARNING" ? "text-amber-600" : "text-gray-500"}`}>
                    <span className="font-bold">{r.severity}:</span> {r.message}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === "edit" && (
          <div className="space-y-2">
            <select data-testid="shift-detail-edit-template" value={form.shift_template_id} onChange={(e) => applyTemplate(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm">
              <option value="">Manual times (or pick a template)</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.code}</option>)}
            </select>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input data-testid="shift-detail-edit-start" type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
              <input data-testid="shift-detail-edit-end" type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
              <input data-testid="shift-detail-edit-break" type="number" min="0" value={form.break_minutes} onChange={(e) => setForm((f) => ({ ...f, break_minutes: Number(e.target.value) }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" placeholder="Break min" />
            </div>
            <input data-testid="shift-detail-edit-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} placeholder="Role (optional)" className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <Textarea data-testid="shift-detail-edit-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for this edit (required, kept on the audit trail)" rows={2} />
          </div>
        )}

        {mode === "replace" && (
          <div className="space-y-2">
            <select data-testid="shift-detail-replace-employee" value={replaceEmployeeId} onChange={(e) => setReplaceEmployeeId(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm">
              <option value="">Select replacement employee</option>
              {employees.filter((e) => e.id !== assignment.employee_id).map((e) => <option key={e.id} value={e.id}>{e.legal_name}{e.position ? ` (${e.position})` : ""}</option>)}
            </select>
            <Textarea data-testid="shift-detail-replace-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for the swap (required, kept on the audit trail)" rows={2} />
          </div>
        )}

        {mode === "copy" && (
          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select data-testid="shift-detail-copy-employee" value={copyEmployeeId} onChange={(e) => setCopyEmployeeId(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">
                {employees.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
              </select>
              <input data-testid="shift-detail-copy-date" type="date" value={copyDate} onChange={(e) => setCopyDate(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            </div>
            <p className="text-xs text-gray-500">Same start/end/break/role as this shift ({assignment.start_time}-{assignment.end_time}) will be copied onto the selected employee/date.</p>
          </div>
        )}

        {mode === "remove" && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">This shift will be removed from the draft schedule. A reason is required and is kept on the audit trail.</p>
            <Textarea data-testid="shift-detail-remove-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for removing this assignment (required)" rows={2} />
          </div>
        )}

        <DialogFooter>
          <button data-testid="shift-detail-cancel" onClick={() => onOpenChange(false)} disabled={submitting} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50">Close</button>
          {mode === "edit" && (
            <button data-testid="shift-detail-save-edit" disabled={submitting || !reason.trim()} onClick={() => run(() => onEdit(assignment, form, reason.trim()))} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
              {submitting ? "Saving..." : "Save Changes"}
            </button>
          )}
          {mode === "replace" && (
            <button data-testid="shift-detail-save-replace" disabled={submitting || !replaceEmployeeId || !reason.trim()} onClick={() => run(() => onReplace(assignment, replaceEmployeeId, reason.trim()))} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
              {submitting ? "Replacing..." : "Replace Employee"}
            </button>
          )}
          {mode === "copy" && (
            <button data-testid="shift-detail-save-copy" disabled={submitting || !copyDate || !copyEmployeeId} onClick={() => run(() => onCopy(assignment, copyEmployeeId, copyDate))} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
              {submitting ? "Copying..." : "Copy Shift"}
            </button>
          )}
          {mode === "remove" && (
            <button data-testid="shift-detail-confirm-remove" disabled={submitting || !reason.trim()} onClick={() => run(() => onRemove(assignment, reason.trim()))} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
              {submitting ? "Removing..." : "Confirm Removal"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
